import { Button, cn } from "@tellescope/ui";
import { useIsRouteActive, useResolveRoutePath, type RouteConfig } from "../../../routing";
import { Link } from "../../../components";
import { NAV_ITEM_BUTTON_STYLES } from "./constants";
import { ReactNode, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { checkIsCurrentFeature } from "../../../utils";
import { getFeatureDisplayName, useOrgFeatures } from "../../../permissions";

export interface SimpleNavItemProps extends RouteConfig {
  path: string;
  children?: ReactNode;
  isActive?: boolean;
}

export const SimpleNavItem = ({
  label,
  icon,
  path,
  children,
  isActive: providedIsActive,
  shouldShow,
  featureName,
}: SimpleNavItemProps) => {
  const location = useLocation();
  const resolvedPath = useResolveRoutePath(path);
  const { plan } = useOrgFeatures();

  // Compute these values on every render based on current location
  const isCurrentFeature = useMemo(
    () => checkIsCurrentFeature(location.pathname, location.search, path) && !location.pathname.endsWith("new"),
    [location.pathname, location.search, path]
  );
  const calculatedIsActive = useIsRouteActive(path);
  const isActive = providedIsActive === undefined ? calculatedIsActive : providedIsActive;

  const trackingId = `nav-link-${path.replace(/\//g, "-").replace(/^-/, "")}`;
  const testId = trackingId;

  if (shouldShow === false) {
    return null;
  }

  const labelOverride = featureName ? getFeatureDisplayName({ plan, featureName }) : undefined;

  return (
    <Link
      to={resolvedPath}
      className="block w-full flex-shrink-0"
      keepSearch={isCurrentFeature}
      data-trackingid={trackingId}
      data-testid={testId}
    >
      <Button variant={isActive ? "secondary" : "ghost"} className={cn(NAV_ITEM_BUTTON_STYLES, "justify-between")}>
        <div className={cn("flex gap-4 flex-1 items-center")}>
          {icon}
          {labelOverride || label?.()}
        </div>
        {children}
      </Button>
    </Link>
  );
};
